import { useInfiniteQuery, type QueryKey } from '@tanstack/react-query'
import { useCallback, useEffect, useRef } from 'react'

import { get } from '@/lib/api'

/**
 * 分页响应类型
 */
export interface PaginatedResponse<T> {
  data: T[]
  meta: {
    page: number
    pageSize: number
    total: number
    totalPages: number
  }
}

/**
 * 无限滚动配置选项
 */
export interface UseInfiniteTasksOptions<T> {
  /** 查询键 */
  queryKey: QueryKey
  /** API 端点 */
  endpoint: string
  /** 额外的查询参数 */
  params?: Record<string, string | number | boolean | undefined>
  /** 每页数量，默认 20 */
  pageSize?: number
  /** 是否启用 */
  enabled?: boolean
  /** 数据转换函数 */
  transform?: (data: T[]) => T[]
}

/**
 * 无限滚动返回值
 */
export interface UseInfiniteTasksReturn<T> {
  /** 所有已加载的数据 */
  data: T[]
  /** 是否正在加载首页 */
  isLoading: boolean
  /** 是否正在加载下一页 */
  isFetchingNextPage: boolean
  /** 是否有下一页 */
  hasNextPage: boolean
  /** 加载下一页 */
  fetchNextPage: () => void
  /** 错误信息 */
  error: Error | null
  /** 总数量 */
  total: number
  /** 是否已加载完所有数据 */
  isComplete: boolean
  /** 滚动容器 ref */
  scrollRef: React.RefObject<HTMLDivElement | null>
}

/**
 * 无限滚动 Hook
 * 
 * 使用 TanStack Query 的 useInfiniteQuery 实现无限滚动加载
 * 
 * Requirements: 6.1, 6.2, 6.3, 6.4
 */
export function useInfiniteTasks<T>({
  queryKey,
  endpoint,
  params = {},
  pageSize = 20,
  enabled = true,
  transform,
}: UseInfiniteTasksOptions<T>): UseInfiniteTasksReturn<T> {
  const scrollRef = useRef<HTMLDivElement>(null)

  const query = useInfiniteQuery({
    queryKey: [...queryKey, { pageSize, ...params }],
    queryFn: async ({ pageParam = 1 }) => {
      const queryParams = new URLSearchParams()
      queryParams.set('page', String(pageParam))
      queryParams.set('pageSize', String(pageSize))
      
      // 添加额外参数
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.set(key, String(value))
        }
      })
      
      const url = `${endpoint}?${queryParams.toString()}`
      return get<PaginatedResponse<T>>(url)
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage?.meta) {return undefined}
      const { page, totalPages } = lastPage.meta
      return page < totalPages ? page + 1 : undefined
    },
    enabled,
  })

  // 合并所有页面的数据
  const allData = query.data?.pages.flatMap((page) => page?.data ?? []) ?? []
  const transformedData = transform ? transform(allData) : allData

  // 获取总数
  const total = query.data?.pages[0]?.meta?.total ?? 0

  // 是否已加载完所有数据
  const isComplete = !query.hasNextPage && !query.isLoading

  // 滚动到底部时自动加载 (Requirement 6.2)
  const handleScroll = useCallback(() => {
    if (!scrollRef.current) {return}
    if (query.isFetchingNextPage || !query.hasNextPage) {return}

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current
    // 距离底部 100px 时触发加载
    if (scrollHeight - scrollTop - clientHeight < 100) {
      void query.fetchNextPage()
    }
  }, [query])

  // 监听滚动事件
  useEffect(() => {
    const element = scrollRef.current
    if (!element) {return}

    element.addEventListener('scroll', handleScroll)
    return () => element.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  return {
    data: transformedData,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: () => void query.fetchNextPage(),
    error: query.error,
    total,
    isComplete,
    scrollRef,
  }
}

/**
 * 无限滚动加载指示器组件 Props
 */
export interface InfiniteScrollIndicatorProps {
  /** 是否正在加载 */
  isLoading: boolean
  /** 是否有更多数据 */
  hasMore: boolean
  /** 是否已完成加载 */
  isComplete: boolean
  /** 加载更多回调 */
  onLoadMore?: () => void
  /** 已加载数量 */
  loadedCount: number
  /** 总数量 */
  totalCount: number
}

/**
 * 计算加载进度
 */
export function calculateLoadProgress(
  loadedCount: number,
  totalCount: number
): { percentage: number; remaining: number } {
  if (totalCount === 0) {
    return { percentage: 100, remaining: 0 }
  }
  const percentage = Math.round((loadedCount / totalCount) * 100)
  const remaining = totalCount - loadedCount
  return { percentage, remaining }
}
